import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Sparkles, Library, CalendarClock, Layers, Coins, ArrowRight, Film } from "lucide-react";
import { HowItWorksButton } from "@/components/HowItWorks";
import ReviewDialog from "@/components/ReviewDialog";
import CommunityGoalWidget from "@/components/CommunityGoalWidget";
import { WelcomeTour } from "@/components/WelcomeTour";

const REVIEW_SNOOZE_KEY = "vio_review_snooze";

function StyleAnalytics() {
  const [data, setData] = useState(null);
  useEffect(() => {
    api.get("/analytics/caption-styles").then((r) => setData(r.data)).catch(() => {});
  }, []);
  if (!data || !data.total_published || data.styles.length === 0) return null;
  const max = Math.max(...data.styles.map((s) => s.views), 1);
  return (
    <div className="border border-white/10 rounded-md bg-[#121212] p-6" data-testid="style-analytics">
      <div className="flex items-center justify-between mb-1">
        <h2 className="font-head text-xl font-semibold">Caption style performance</h2>
        <span className="text-xs text-zinc-500">{data.total_published} published</span>
      </div>
      <p className="text-xs text-zinc-500 mb-5">Which caption style your published videos use — and the YouTube views each one is pulling.</p>
      <div className="space-y-4">
        {data.styles.map((s) => (
          <div key={s.style} data-testid={`style-analytics-row-${s.style}`}>
            <div className="flex items-center justify-between text-sm mb-1.5 gap-3">
              <span className="font-semibold truncate">{s.label} <span className="text-zinc-500 font-normal">· {s.videos} video{s.videos === 1 ? "" : "s"}</span></span>
              <span className="text-zinc-400 shrink-0" data-testid={`style-analytics-views-${s.style}`}>
                {s.views.toLocaleString()} views{s.avg_views != null ? ` · avg ${s.avg_views.toLocaleString()}` : ""}
              </span>
            </div>
            <div className="h-2 rounded-full bg-white/5 overflow-hidden">
              <div className="h-full rounded-full bg-blue-500 transition-[width] duration-500" style={{ width: `${Math.max(4, (s.views / max) * 100)}%` }} />
            </div>
          </div>
        ))}
      </div>
      {!data.connected && <p className="text-xs text-zinc-600 mt-4">Connect YouTube in Settings to pull live view counts.</p>}
    </div>
  );
}

export default function Dashboard() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [recent, setRecent] = useState([]);
  const [reviewOpen, setReviewOpen] = useState(false);

  useEffect(() => {
    api.get("/stats").then((r) => {
      setStats(r.data);
      if ((r.data?.videos ?? 0) < 1) return;
      const snooze = Number(localStorage.getItem(REVIEW_SNOOZE_KEY) || 0);
      if (Date.now() < snooze) return;
      api.get("/reviews/me").then((res) => {
        if (!res.data.reviewed) setTimeout(() => setReviewOpen(true), 2500);
      }).catch(() => {});
    }).catch(() => {});
    api.get("/videos").then((r) => setRecent(r.data.slice(0, 4))).catch(() => {});
  }, []);

  const closeReview = (submitted) => {
    setReviewOpen(false);
    if (!submitted) localStorage.setItem(REVIEW_SNOOZE_KEY, String(Date.now() + 3 * 24 * 3600 * 1000));
  };

  const cards = [
    { label: "Videos created", value: stats?.videos ?? 0, icon: Film },
    { label: "Scheduled", value: stats?.scheduled ?? 0, icon: CalendarClock },
    { label: "Published", value: stats?.published ?? 0, icon: ArrowRight },
    { label: "Series", value: stats?.series ?? 0, icon: Layers },
  ];

  return (
    <div className="space-y-8" data-testid="dashboard">
      <WelcomeTour />
      <ReviewDialog open={reviewOpen} onClose={closeReview} />
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-4">
        <div>
          <p className="text-xs uppercase tracking-[0.2em] text-zinc-500 mb-2">Control room</p>
          <h1 className="font-head text-3xl md:text-4xl font-bold tracking-tight">
            Welcome back, {user?.name?.split(" ")[0] || "creator"}.
          </h1>
        </div>
        <div className="flex items-center gap-3">
          <HowItWorksButton variant="dashboard" />
          <Link to="/create" data-testid="dashboard-create-btn" className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-500 px-5 py-2.5 rounded-md font-semibold transition-all duration-200 hover:-translate-y-0.5">
            <Sparkles className="h-4 w-4" /> New video
          </Link>
        </div>
      </div>

      {/* Stat grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {cards.map((c) => (
          <div key={c.label} className="border border-white/10 rounded-md bg-[#121212] p-5 hover:border-white/20 transition-colors">
            <c.icon className="h-5 w-5 text-blue-400 mb-4" />
            <p className="font-head text-3xl font-bold">{c.value}</p>
            <p className="text-xs text-zinc-500 mt-1">{c.label}</p>
          </div>
        ))}
      </div>

      {/* Credits + quick actions */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="border border-blue-500/30 rounded-md bg-blue-600/10 p-6 flex flex-col justify-between">
          <div>
            <Coins className="h-6 w-6 text-blue-400 mb-3" />
            <p className="font-head text-4xl font-black" data-testid="dashboard-credits">{stats?.credits ?? user?.credits ?? 0}</p>
            <p className="text-sm text-zinc-400">credits available</p>
          </div>
          <Link to="/credits" className="mt-4 text-sm text-blue-400 hover:text-blue-300 inline-flex items-center gap-1">
            Get more <ArrowRight className="h-3.5 w-3.5" />
          </Link>
        </div>
        <QuickAction to="/create" icon={Sparkles} title="Forge a video" desc="Script → voice → visuals in minutes." />
        <QuickAction to="/schedule" icon={CalendarClock} title="Schedule a post" desc="Queue to YouTube, TikTok & Instagram." />
      </div>

      {/* Community goal */}
      <CommunityGoalWidget />

      {/* Caption style performance */}
      <StyleAnalytics />

      {/* Recent */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-head text-xl font-semibold">Recent videos</h2>
          <Link to="/library" className="text-sm text-zinc-400 hover:text-white">View all</Link>
        </div>
        {recent.length === 0 ? (
          <div className="border border-dashed border-white/10 rounded-md p-12 text-center">
            <Film className="h-8 w-8 text-zinc-600 mx-auto mb-3" />
            <p className="text-zinc-400">No videos yet.</p>
            <Link to="/create" className="text-blue-400 hover:text-blue-300 text-sm">Create your first →</Link>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {recent.map((v) => (
              <Link key={v.id} to={`/video/${v.id}`} data-testid={`recent-video-${v.id}`} className="border border-white/10 rounded-md bg-[#121212] p-5 hover:border-white/20 transition-colors flex items-center justify-between gap-4">
                <div className="min-w-0">
                  <p className="font-medium truncate">{v.title}</p>
                  <p className="text-xs text-zinc-500 mt-1">{v.platform} · {v.scenes?.length || 0} scenes · {v.status}</p>
                </div>
                <ArrowRight className="h-4 w-4 text-zinc-500 shrink-0" />
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function QuickAction({ to, icon: Icon, title, desc }) {
  return (
    <Link to={to} className="border border-white/10 rounded-md bg-[#121212] p-6 hover:border-white/20 transition-colors group">
      <Icon className="h-6 w-6 text-blue-400 mb-3 group-hover:scale-110 transition-transform" />
      <p className="font-head text-lg font-semibold">{title}</p>
      <p className="text-sm text-zinc-500 mt-1">{desc}</p>
    </Link>
  );
}
